<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { MarDashCard, MarIconBtn, MarTag, openModal } from '@nimling/components-nimtech'
import { isGuest, type ViewData } from '@/lib/api'
import { getSignalTone } from '@/lib/view'
import type { PageView, View } from '@/types'
import AnnotationDialog from './dialogs/AnnotationDialog.vue'
import PlacementMenu, { type PlacementMenuPick } from './dialogs/PlacementMenu.vue'
import { useSettingsStore } from '@/stores/settings'
import ViewMount from './ViewMount.vue'

const props = withDefaults(defineProps<{
  view: View
  pageView: PageView
  data: ViewData | null
  mounted?: boolean
  editable?: boolean
}>(), {
  mounted: true,
  editable: false,
})

const emit = defineEmits<{ remove: []; settings: [] }>()

const settingsStore = useSettingsStore()

const stateKey = computed(() => `${props.pageView.page_id}:${props.pageView.id}`)

const gearEl = ref<{ $el: HTMLElement } | null>(null)
const menuOpen = ref(false)
const pointed = ref(false)
const focused = ref(false)

const openMenu = async () => {
  const anchor = gearEl.value?.$el
  if (!anchor) return
  menuOpen.value = true
  const pick = await openModal<PlacementMenuPick>({
    kind: 'dropdown',
    component: PlacementMenu,
    anchor: { target: anchor },
    placement: { side: 'bottom', align: 'end', offset: 4, minWidth: '11em' },
    backdrop: 'invisible',
  })
  menuOpen.value = false
  if (pick === 'settings') emit('settings')
  if (pick === 'delete') emit('remove')
}

const onKey = (e: KeyboardEvent) => {
  if (menuOpen.value || e.key.toLowerCase() !== 'd' || e.metaKey || e.ctrlKey || e.altKey) return
  const target = e.target as HTMLElement | null
  if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
  e.preventDefault()
  emit('remove')
}

const armed = computed(() => props.editable && (pointed.value || focused.value))

watch(armed, (on) => {
  if (on) window.addEventListener('keydown', onKey)
  else window.removeEventListener('keydown', onKey)
})

onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

const onMark = async (time: string) => {
  if (props.editable || isGuest()) return
  await openModal<'saved'>({
    kind: 'dialog',
    component: AnnotationDialog,
    props: { pageViewId: props.pageView.id, viewName: props.view.name, time },
  })
}
</script>

<template>
  <MarDashCard variant="glass" class="tile" :class="{ 'tile-editable': editable }" :operable="!editable" @pointerenter="pointed = true" @pointerleave="pointed = false" @focusin="focused = true" @focusout="focused = false">
    <template #head>
      <span class="tile-name" :title="view.description ? `${view.name}: ${view.description}` : view.name">{{ view.name }}</span>
      <span class="tile-signal" :title="`Reads ${view.signal} from the store`"><MarTag :label="view.signal" :severity="getSignalTone(view.signal)" rounded /></span>
      <span v-if="!editable && settingsStore.hasTableStates(stateKey)" class="tile-actions" @pointerdown.stop>
        <MarIconBtn icon="refresh" tooltip="Reset the table columns of this view" @click="settingsStore.deleteTableStates(stateKey)" />
      </span>
      <span v-if="editable" class="tile-gear" @pointerdown.stop>
        <MarIconBtn ref="gearEl" icon="settings" tooltip="Placement options" @click="openMenu" />
      </span>
    </template>
    <div class="tile-body">
      <ViewMount v-if="mounted" :component="view.component ?? ''" :style-text="view.style ?? ''" :data="data" :state-key="stateKey" @mark="onMark" />
    </div>
  </MarDashCard>
</template>

<style scoped>
.tile-name {
  align-self: center;
  flex: 0 1 auto;
  min-width: 0;
  font-size: 0.8125em;
  font-weight: 600;
  color: var(--mar-text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tile-gear {
  align-self: center;
  flex: none;
  margin-left: auto;
  display: flex;
}

.tile-signal {
  align-self: center;
  flex: none;
  display: flex;
}

.tile-actions {
  align-self: center;
  flex: none;
  margin-left: auto;
  display: flex;
  gap: 0.125em;
}

.tile-body {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.tile-editable .tile-body {
  pointer-events: none;
}

.tile .tile-actions {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.tile:hover .tile-actions,
.tile:focus-within .tile-actions {
  opacity: 1;
}
</style>

<style>
[data-page-bare] .tile,
[data-page-bare] .tile .view-root > * {
  border-radius: 0;
}

[data-page-bare] .tile:not(.tile-editable) > .mar-dash-card-head {
  display: none;
}

[data-page-bare] .tile:not(.tile-editable) > .mar-dash-card-body {
  --view-inset-top: var(--view-inset);
}
</style>
